"use client";

import { useLocale } from "@/i18n/LocaleProvider";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: React.ReactNode;
  /** Body content; usually a confirmation message. */
  children?: React.ReactNode;
  /** Optional warning line shown below the body in a danger style, e.g. the
   * "this cannot be undone" notice for destructive actions. */
  warning?: React.ReactNode;
  /** Label for the confirm button. Defaults to the locale's generic confirm. */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to the locale's generic cancel. */
  cancelLabel?: string;
  /** Shows a spinner on the confirm button and disables both buttons. */
  loading?: boolean;
  /** Disables the confirm button (e.g. while a precondition is unmet). */
  confirmDisabled?: boolean;
  /** Drops the cancel button, leaving a single action. Use when there is
   * nothing left to cancel — e.g. after the confirmed action has completed
   * and the dialog is only reporting the result. */
  hideCancel?: boolean;
  /** Renders the confirm button in the danger style (destructive actions). */
  danger?: boolean;
}

/**
 * Reusable confirmation modal built on top of `Modal`. Owns nothing but the
 * two footer buttons — the caller controls `open`, `loading` and the
 * `onConfirm`/`onClose` handlers, so the same component backs "delete package
 * type", "delete receipt", etc. without per-use special casing.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  children,
  warning,
  confirmLabel,
  cancelLabel,
  loading,
  confirmDisabled,
  hideCancel,
  danger,
}: ConfirmDialogProps) {
  const { dict } = useLocale();
  const t = dict.confirm;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          {!hideCancel && (
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={loading}
            >
              {cancelLabel ?? t.cancel}
            </Button>
          )}
          <Button
            type="button"
            variant={danger ? "danger" : "primary"}
            onClick={onConfirm}
            loading={loading}
            disabled={confirmDisabled}
          >
            {confirmLabel ?? t.confirm}
          </Button>
        </>
      }
    >
      {children}
      {warning && (
        <p className="mt-3 rounded-lg bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
          {warning}
        </p>
      )}
    </Modal>
  );
}
